import { useState } from 'react';

import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useNavigate } from 'react-router';

import { GetSectionsItem, useGetSections } from 'api/hooks/private/sections/GetSections';

import { PageContainer } from 'components/PageContainer';

dayjs.extend(utc);
dayjs.extend(timezone);

const columns: GridColDef<GetSectionsItem>[] = [
  {
    field: 'title',
    type: 'string',
    headerName: 'Title',
    minWidth: 300,
  },
  {
    field: 'total_enrollment_count',
    type: 'number',
    headerName: 'Total Enrolled',
    minWidth: 150,
    valueGetter: (_, row) => Number(row.total_enrollment_count),
  },
  {
    field: 'capacity',
    type: 'number',
    headerName: 'Capacity',
    minWidth: 150,
    valueFormatter: (_, row) => (row.capacity === -1 ? 'No Limit' : row.capacity),
  },
  {
    field: 'open_enrollment',
    type: 'dateTime',
    headerName: 'Open Enrollment',
    minWidth: 250,
    valueGetter: (_, row) => (row.open_enrollment ? new Date(row.open_enrollment) : null),
    valueFormatter: (_, row) =>
      row.open_enrollment ? dayjs(row.open_enrollment).format('MMM Do YYYY, h:mm a z') : null,
  },
  {
    field: 'close_enrollment',
    type: 'dateTime',
    headerName: 'Close Enrollment',
    minWidth: 250,
    valueGetter: (_, row) => (row.close_enrollment ? new Date(row.close_enrollment) : null),
    valueFormatter: (_, row) =>
      row.close_enrollment ? dayjs(row.close_enrollment).format('MMM Do YYYY, h:mm a z') : null,
  },
  {
    field: 'tenant_location',
    type: 'string',
    headerName: 'Location',
    minWidth: 250,
    valueGetter: (_, row) => row.tenant_location?.name,
  },
];

export function SectionList() {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 25,
    page: 0,
  });

  const navigate = useNavigate();

  // fetch sections
  const sections = useGetSections({
    variables: {
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
    },
  });

  return (
    <PageContainer title="Manage Sections">
      <DataGrid
        columns={columns}
        rows={sections.data?.rows}
        loading={sections.isPending}
        onRowClick={(params) => navigate(`/admin/sections/${params.row.id}`)}
        // enable serverside pagination
        paginationMode="server"
        rowCount={sections.data?.count ?? 0}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        disableColumnFilter
        disableColumnSorting
      />
    </PageContainer>
  );
}

export default SectionList;
